import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Layout Components
import Layout from './components/Layout/Layout';

// Auth Components
import Login from './components/Auth/Login';
import ForcePasswordChange from './components/Auth/ForcePasswordChange';
import Register from './components/Auth/Register';

// Page Components
import Dashboard from './pages/Dashboard';
import PreLeads from './pages/PreLeads';
import Appointments from './pages/Appointments';
import RecallQueue from './pages/RecallQueue';
import TeamManagement from './pages/TeamManagement';
import Settings from './pages/Settings';
import MasterData from './pages/MasterData';
import Calendar from './pages/Calendar';
import CallReport from './pages/CallReport';
import Reports from './pages/Reports';
import SolarRGF from './pages/SolarRGF';
import SolarRGFList from './pages/SolarRGFList';
import LeadDetail from './pages/LeadDetail';

// Context
import { AuthProvider } from './context/AuthContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0B63C9', // Brand blue
    },
    secondary: {
      main: '#FF9800', // Orange
    },
    success: {
      main: '#4CAF50', // Green
    },
    warning: {
      main: '#FFC107', // Amber
    },
    error: {
      main: '#F44336', // Red
    },
    background: {
      default: '#F5F7FA', // Light Gray
    },
    text: {
      primary: '#333333', // Dark Gray
    },
  },
  typography: {
    fontFamily: "'IBM Plex Sans', Roboto, Arial, sans-serif",
    h1: {
      fontSize: '24px',
      fontWeight: 500,
    },
    h2: {
      fontSize: '20px',
      fontWeight: 500,
    },
    h3: {
      fontSize: '18px',
      fontWeight: 500,
    },
    body1: {
      fontSize: '14px',
    },
    body2: {
      fontSize: '12px',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          textTransform: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

function App() {
  // Check if user is authenticated
  const isAuthenticated = () => {
    return localStorage.getItem('token') !== null;
  };

  // Protected route component
  const ProtectedRoute = ({ children }) => {
    const forceChange = localStorage.getItem('forcePasswordChange') === 'true';
    if (forceChange) return <ForcePasswordChange onSuccess={() => { localStorage.removeItem('forcePasswordChange'); window.location.reload(); }} />;
    if (!isAuthenticated()) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  // Smart home redirect based on permissions
  const HomeRedirect = () => {
    const allPerms = (() => { try { return JSON.parse(localStorage.getItem('allPermissions')||'{}'); } catch(e) { return {}; } })();
    const user = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}'); } catch(e) { return {}; } })();
    const role = user?.role;
    const isAdmin = role === 'admin' || role === 'superadmin';
    if (isAdmin) return <Dashboard />;
    const rolePerms = allPerms[role] || {};
    const permsLoaded = Object.keys(rolePerms).length > 0;
    if (!permsLoaded) return <Dashboard />;
    // Check if dashboard is permitted
    if (rolePerms['dashboard']?.view) return <Dashboard />;
    // Redirect to first permitted module
    const routes = [
      { key:'preleads', path:'/preleads' },
      { key:'calendar', path:'/calendar' },
      { key:'appointments', path:'/appointments' },
      { key:'recalls', path:'/recalls' },
      { key:'team', path:'/team' },
      { key:'masterdata', path:'/master-data' },
      { key:'callreport', path:'/reports/calls' },
      { key:'settings', path:'/settings' },
    ];
    const first = routes.find(r => rolePerms[r.key]?.view);
    if (first) return <Navigate to={first.path} replace />;
    return <Dashboard />; // fallback
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<HomeRedirect />} />
              <Route path="preleads" element={<PreLeads />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="reports/calls" element={<CallReport />} />
              <Route path="reports" element={<Reports />} />
              <Route path="solar-rgf" element={<SolarRGF />} />
              <Route path="solar-rgf-list" element={<SolarRGFList />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="recalls" element={<RecallQueue />} />
              <Route path="team" element={<TeamManagement />} />
              <Route path="settings" element={<Settings />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="master-data" element={<MasterData/>}/>
            </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
